import React, {createRef, Fragment, useEffect, useState} from "react";
import {Card, Table, Space, Divider, Menu, Dropdown, Form, Tooltip} from "antd";
import {TableProps} from "antd/lib/table";
import {
    RedoOutlined,
    ColumnHeightOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
} from "@ant-design/icons";
import * as Screenfull from "screenfull";


import styles from "./index.module.css";
import {SizeType} from "antd/lib/config-provider/SizeContext";
import SettingPopover, {ColumnProps} from "./settingPopover";
import SearchBar, {ColumnType as SearchableColumnType} from "./searchBar";
import {FormInstance} from "antd/lib/form";
import {EditableColumnType, EditCellComponent, EditType} from "./editTable";

type ColumnType = SearchableColumnType & EditableColumnType & { permission?: string | undefined };

export type StandardColumnsType = ColumnType[]

interface StandardTableProps extends TableProps<any> {
    clickEditable?: boolean
    cacheKey?: string
    editingKey?: any
    toolbar?: JSX.Element
    submit?: Function
    form?: FormInstance
    editForm?: FormInstance
    columns: ColumnType[]
    showCard?: boolean
    showTools?: boolean
    onColumnsChange?: (columns: ColumnProps[]) => void
    onEditChange?: (dataIndex: string, value: any) => void
    onEditClick?: (rowKey: any, record: any) => void
}

function DivComponent(props: any) {
    const {style, ...otherProps} = props
    return <div style={{background: 'white', ...style}} {...otherProps}/>
}

function StandardTable(props: StandardTableProps) {

    const {
        cacheKey,
        rowKey,
        editingKey,
        toolbar,
        submit,
        columns,
        form,
        editForm,
        showCard = true,
        showTools = true,
        onColumnsChange,
        onEditChange,
        onEditClick,
        clickEditable = false,
        ...otherProps
    } = props
    const [selectedSize, setSelectedSize] = useState('default')
    const [isFullscreen, setFullscreen] = useState(false)
    const [topColumns, setTopColumns] = useState<ColumnProps[]>([])
    const [midColumns, setMidColumns] = useState<ColumnProps[]>([])
    const [bottomColumns, setBottomColumns] = useState<ColumnProps[]>([])
    const [realColumns, setRealColumns] = useState<ColumnProps[]>([])


    let ref = createRef<HTMLDivElement>()

    useEffect(() => {
        let rc = [...topColumns, ...midColumns, ...bottomColumns].filter(item => item.checked)
        rc = rc.map((col: any) => {
            if (!col.editable) {
                return col
            }
            return {
                ...col,
                onCell: (record: any, rowIndex: number) => {
                    const key = (rowKey instanceof Function ? rowKey(record) : rowKey) || 'id'
                    return {
                        record,
                        rowIndex,
                        editable: (col.editable instanceof Function ? col.editable(record) : col.editable),
                        clickable: clickEditable,
                        onEditClick: () => onEditClick?.(record[key], record),
                        editing: editingKey === record[key],
                        dataIndex: col.editIndex || col.dataIndex,
                        editType: col.editType || EditType.text,
                        editRules: col.editRules || [],
                        editValueEnum: col.editValueEnum || {},
                        editComponentProps: col.editComponentProps || {},
                        editRender: col.editRender,
                        onEditChange
                    }
                }
            }
        })
        setRealColumns(rc)
        onColumnsChange?.(rc)
    }, [topColumns, midColumns, bottomColumns])

    function onRefresh() {
        submit && submit()
    }

    const sizeMenus = (
        <Menu selectedKeys={[selectedSize]} onClick={({key}) => {
            setSelectedSize(key.toString())
        }}>
            <Menu.Item key={'default'}>Large</Menu.Item>
            <Menu.Item key={'middle'}>Middle</Menu.Item>
            <Menu.Item key={'small'}>Compact</Menu.Item>
        </Menu>
    )

    function onFullscreen() {
        if (Screenfull.isEnabled && ref.current) {
            Screenfull.toggle(ref.current).then(() => {
                setFullscreen(!isFullscreen)
            })
        }
    }

    function onSubmit() {
        submit && submit()
    }

    const searchColumns = columns.filter((item: any) => item.search)

    const CardElement = showCard ? Card : DivComponent

    const components = {
        body: {
            cell: EditCellComponent
        }
    }
    return (
        <div ref={ref} style={{background: '#f0f2f5'}}>
            {(searchColumns && searchColumns.length > 0 && form) && (
                <CardElement style={showCard ? {marginBottom: 15} : {paddingBottom: 15}}>
                    <SearchBar form={form} columns={searchColumns} onSubmit={onSubmit}/>
                </CardElement>
            )}
            <CardElement>
                <div className={styles.toolbar}
                     style={showTools ? {} : {height: 0, overflow: "hidden", marginBottom: 0}}>
                    <div className={styles.operations}>
                        {toolbar && (
                            <>
                                {toolbar}
                                <Divider style={{margin: '0 15px'}} type={"vertical"}/>
                            </>
                        )}
                    </div>
                    <Space size={25}>
                        {submit && <div className={styles.toolbarItem} onClick={onRefresh}><Tooltip
                            title={'Refresh'}><RedoOutlined/></Tooltip></div>}
                        <div className={styles.toolbarItem}>
                            <Tooltip title={'Adjust Line Gap'}><Dropdown trigger={['click']}
                                                                         overlay={sizeMenus}><ColumnHeightOutlined/></Dropdown></Tooltip>
                        </div>
                        <div>
                            <Tooltip title={'Hide or Lock the Column'}>
                                <SettingPopover cacheKey={cacheKey} columns={columns} onChange={(top, mid, bottom) => {
                                    setTopColumns(top)
                                    setMidColumns(mid)
                                    setBottomColumns(bottom)
                                }}/>
                            </Tooltip>
                        </div>
                        <div className={styles.toolbarItem} onClick={onFullscreen}>
                            <Tooltip title={'Zoom In and Out'}>
                                {isFullscreen ? <FullscreenExitOutlined/> : <FullscreenOutlined/>}
                            </Tooltip>
                        </div>
                    </Space>
                </div>
                <Form form={editForm} component={false}>
                    <Table rowKey={rowKey} components={components} size={selectedSize as SizeType}
                           sticky
                           rowClassName={'editable-row'}
                           columns={realColumns as any} {...otherProps} />
                </Form>
            </CardElement>
        </div>
    )
}

export default StandardTable
